/**
 * Advies kostprijs & verkoopprijs (lostaand van WooCommerce importprijs).
 * Valuta leverancier: EUR of USD (uit leverancier.valuta).
 */

export const DEFAULT_PRIJSBEREKENING_SETTINGS = {
  eur_per_usd: 0.92,
  container_total: 0,
  container_currency: 'EUR', // 'EUR' | 'USD'
  container_cbm_max: 68,
  verzend_dpd: 0,
  verzend_dhl: 0,
  verzend_pallet: 0,
  marge_pct: 0, // standaard; overschreven door leverancier.prijsberekening_marge_pct indien gezet
  marketing_pct: 0,
  extra_per_stuk_standaard: 0, // alleen als product.prijsberekening_extra_per_stuk leeg is
  dealer_korting_pct: 0,
  btw_pct: 21, // BTW over consumentenadvies (ex → incl); dealer blijft ex BTW
  rond_advies_incl_btw_op_punt95: true, // advies consument (incl. BTW) naar volgende €X,95
};

export function parsePrijsberekeningSettings(raw) {
  if (!raw || typeof raw !== 'string') return { ...DEFAULT_PRIJSBEREKENING_SETTINGS };
  try {
    const j = JSON.parse(raw);
    return {
      ...DEFAULT_PRIJSBEREKENING_SETTINGS,
      ...j,
      container_currency: String(j.container_currency || 'EUR').toUpperCase() === 'USD' ? 'USD' : 'EUR',
    };
  } catch {
    return { ...DEFAULT_PRIJSBEREKENING_SETTINGS };
  }
}

/** Handmatige correctie op advies incl. BTW (heel €), geen invloed op kostprijs of dealer. */
export function adviesCorrectieEurInclBtw(product) {
  const v = product?.prijsberekening_advies_correctie_eur;
  if (v == null || v === '') return 0;
  const x = parseFloat(String(v).replace(',', '.'));
  if (!Number.isFinite(x)) return 0;
  return Math.round(x);
}

/** Extra €/stuk: productveld, anders globale standaard. */
export function extraPerStukEur(product, settings) {
  const p = product?.prijsberekening_extra_per_stuk;
  if (p !== null && p !== undefined && String(p).trim() !== '') {
    const x = parseFloat(String(p).replace(',', '.'));
    if (Number.isFinite(x)) return Math.max(0, x);
  }
  return Math.max(0, parseFloat(settings.extra_per_stuk_standaard) || 0);
}

/** Marge %: leverancier veld, anders globaal. */
export function margePctForRow(leverancier, settings) {
  const lv = leverancier?.prijsberekening_marge_pct;
  if (lv !== null && lv !== undefined && String(lv).trim() !== '') {
    const x = parseFloat(String(lv).replace(',', '.'));
    if (Number.isFinite(x)) return Math.max(0, x);
  }
  return Math.max(0, parseFloat(settings.marge_pct) || 0);
}

/** EUR per 1 CBM containerkosten (totale containerprijs / max CBM), al omgekeurd naar EUR. */
export function containerEurPerCbm(settings) {
  const cbm = Math.max(0, parseFloat(settings.container_cbm_max) || 0);
  if (cbm <= 0) return 0;
  const total = Math.max(0, parseFloat(settings.container_total) || 0);
  if (total <= 0) return 0;
  const eurusd = Math.max(0.0001, parseFloat(settings.eur_per_usd) || 0.92);
  const totalEur = settings.container_currency === 'USD' ? total * eurusd : total;
  return totalEur / cbm;
}

/**
 * Afronden naar psychologische prijs: omhoog naar het volgende bedrag dat eindigt op ,95
 * (bijv. 32,12 → 32,95; 32,95 → 32,95; 33,01 → 33,95).
 */
export function roundInclBtwToNinetyFiveCents(eur) {
  const x = Number(eur);
  if (!Number.isFinite(x) || x <= 0) return x;
  const stepped = Math.ceil((x - 0.95) / 1) * 1 + 0.95;
  return Math.round(stepped * 100) / 100;
}

export function verzendToeslagEur(product, settings) {
  const vt = String(product?.verzendtype_leverbelofte || '').trim().toLowerCase();
  if (vt === 'dhl') return Math.max(0, parseFloat(settings.verzend_dhl) || 0);
  if (vt === 'pallet') return Math.max(0, parseFloat(settings.verzend_pallet) || 0);
  if (vt === 'standaard') return Math.max(0, parseFloat(settings.verzend_dpd) || 0);
  const sv = String(product?.standaard_vervoerder || '').trim().toLowerCase();
  if (sv === 'dhl') return Math.max(0, parseFloat(settings.verzend_dhl) || 0);
  return Math.max(0, parseFloat(settings.verzend_dpd) || 0);
}

/**
 * @param {object} product
 * @param {object|null} leverancier — record uit /leveranciers met valuta, prijsberekening_marge_pct
 * @param {object} settings — parsed settings
 */
export function computePrijsberekeningRow(product, leverancier, settings) {
  const eurusd = Math.max(0.0001, parseFloat(settings.eur_per_usd) || 0.92);
  const valuta = String(leverancier?.valuta || 'EUR').toUpperCase() === 'USD' ? 'USD' : 'EUR';

  const inkoopRaw = Math.max(0, parseFloat(product?.inkoopprijs) || 0);
  const inkoopEur = valuta === 'USD' ? inkoopRaw * eurusd : inkoopRaw;

  const cbm = Math.max(0, parseFloat(product?.cbm_per_stuk) || 0);
  const eurPerCbmCont = containerEurPerCbm(settings);
  const containerAllocEur = cbm * eurPerCbmCont;

  const kostprijsEur = inkoopEur + containerAllocEur;

  const extra = extraPerStukEur(product, settings);
  const verzend = verzendToeslagEur(product, settings);
  const subVoorMarge = kostprijsEur + extra + verzend;
  const margePct = margePctForRow(leverancier, settings);
  const naMargeEur = subVoorMarge * (1 + margePct / 100);
  const marge_opslag_eur = naMargeEur - subVoorMarge;
  const mktPct = Math.max(0, parseFloat(settings.marketing_pct) || 0);
  const marketingEur = naMargeEur * (mktPct / 100);
  const adviesExBtwEur = naMargeEur + marketingEur;

  const btwPct = Math.max(0, parseFloat(settings.btw_pct) || 0);
  let adviesInclBtwEur = adviesExBtwEur * (1 + btwPct / 100);
  const rond95 = settings.rond_advies_incl_btw_op_punt95 !== false;
  if (rond95) {
    adviesInclBtwEur = roundInclBtwToNinetyFiveCents(adviesInclBtwEur);
  }

  const adviesInclBtwBasisEur = adviesInclBtwEur;
  const adviesCorrectieEur = adviesCorrectieEurInclBtw(product);
  const adviesInclBtwMetCorrectieEur = adviesInclBtwBasisEur + adviesCorrectieEur;

  /** Bruto marge ex BTW na handmatige correctie: verkoop ex BTW afgeleid van advies incl. (na correctie) minus subtotaal vóór marge %. */
  const btwFactorIncl = 1 + btwPct / 100;
  const adviesExBtwEffectiefNaCorrectie =
    btwFactorIncl > 0 ? adviesInclBtwMetCorrectieEur / btwFactorIncl : adviesInclBtwMetCorrectieEur;
  const margeWerkelijkExBtwEur =
    Math.round((adviesExBtwEffectiefNaCorrectie - subVoorMarge) * 100) / 100;

  let dealerKorting = Math.max(0, Math.min(100, parseFloat(settings.dealer_korting_pct) || 0));
  /** Dealer altijd ex BTW, op basis van consumentenprijs ex BTW. */
  const dealerPrijsExBtwEur = adviesExBtwEur * (1 - dealerKorting / 100);

  const verkoopWoo = parseFloat(product?.prijs) || 0;
  /** Woo-verkoopprijs wordt als incl. BTW beschouwd (consumentenwebshop). Diff t.o.v. advies incl. na handmatige correctie. */
  const margeWooVsAdviesIncl = verkoopWoo > 0 ? verkoopWoo - adviesInclBtwMetCorrectieEur : null;

  return {
    valuta,
    inkoop_raw: inkoopRaw,
    inkoop_eur: inkoopEur,
    cbm,
    container_alloc_eur: containerAllocEur,
    kostprijs_eur: kostprijsEur,
    extra_per_stuk: extra,
    verzend_eur: verzend,
    sub_voor_marge_eur: subVoorMarge,
    marge_pct_applied: margePct,
    marge_from_leverancier:
      leverancier?.prijsberekening_marge_pct != null && String(leverancier.prijsberekening_marge_pct).trim() !== '',
    na_marge_eur: naMargeEur,
    marge_opslag_eur,
    marketing_eur: marketingEur,
    advies_verkoop_ex_btw_eur: adviesExBtwEur,
    advies_verkoop_incl_btw_basis_eur: adviesInclBtwBasisEur,
    advies_correctie_eur_incl_btw: adviesCorrectieEur,
    marge_werkelijk_ex_btw_eur: margeWerkelijkExBtwEur,
    advies_verkoop_incl_btw_eur: adviesInclBtwMetCorrectieEur,
    btw_pct_applied: btwPct,
    dealer_korting_pct_applied: dealerKorting,
    dealer_prijs_ex_btw_eur: dealerPrijsExBtwEur,
    verkoop_woo: verkoopWoo,
    marge_vs_advies: margeWooVsAdviesIncl,
  };
}
